import axios from 'axios'
import logger from '@/utils/logger'

const state = {
  properties: [],
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1
  },
  currentProperty: null,
  buildings: [],
  unitTypes: [],
  loading: false,
  error: null
}

const mutations = {
  SET_PROPERTIES(state, { properties, pagination }) {
    state.properties = properties
    state.pagination = pagination
  },

  SET_CURRENT_PROPERTY(state, property) {
    state.currentProperty = property
  },

  SET_BUILDINGS(state, buildings) {
    state.buildings = buildings
  },

  SET_UNIT_TYPES(state, unitTypes) {
    state.unitTypes = unitTypes
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  ADD_PROPERTY(state, property) {
    state.properties.unshift(property)
    state.pagination.total += 1
  },

  UPDATE_PROPERTY(state, updatedProperty) {
    const index = state.properties.findIndex(p => p.id === updatedProperty.id)
    if (index !== -1) {
      state.properties.splice(index, 1, updatedProperty)
    }
    if (state.currentProperty?.id === updatedProperty.id) {
      state.currentProperty = updatedProperty
    }
  },

  DELETE_PROPERTY(state, propertyId) {
    state.properties = state.properties.filter(p => p.id !== propertyId)
    state.pagination.total -= 1
  }
}

const actions = {
  async fetchProperties({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.get('/properties/', { params })
      commit('SET_PROPERTIES', response.data)
      commit('SET_BUILDINGS', response.data.buildings)
      commit('SET_UNIT_TYPES', response.data.unit_types)
      return response.data
    } catch (error) {
      logger.error('Failed to fetch properties from store:', error)

      // Handle authentication errors gracefully
      if (error.response?.status === 401) {
        logger.warn('Authentication failed when fetching properties from store')
        commit('SET_PROPERTIES', { properties: [], pagination: {
          page: 1,
          per_page: 20,
          total: 0,
          pages: 1
        }})
        commit('SET_BUILDINGS', [])
        commit('SET_UNIT_TYPES', [])
        return { properties: [], pagination: {
          page: 1,
          per_page: 20,
          total: 0,
          pages: 1
        }, buildings: [], unit_types: []}
      }

      const errorMessage = error.response?.data?.message || '获取房源列表失败'
      commit('SET_ERROR', errorMessage)
      return { properties: [], pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        pages: 1
      }, buildings: [], unit_types: []}
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchProperty({ commit }, propertyId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.get(`/properties/${propertyId}`)
      commit('SET_CURRENT_PROPERTY', response.data.property)
      return response.data.property
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取房源详情失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createProperty({ commit }, propertyData) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.post('/properties/', propertyData)
      commit('ADD_PROPERTY', response.data.property)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '创建房源失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateProperty({ commit }, { propertyId, propertyData }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.put(`/properties/${propertyId}`, propertyData)
      commit('UPDATE_PROPERTY', response.data.property)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新房源失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteProperty({ commit }, propertyId) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      await axios.delete(`/properties/${propertyId}`)
      commit('DELETE_PROPERTY', propertyId)
      return { success: true }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '删除房源失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async importProperties({ commit, dispatch }, file) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post('/properties/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      // Refresh properties list after import
      await dispatch('fetchProperties')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '导入房源失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async exportProperties(_, params = {}) {
    const response = await axios.get('/properties/export', {
      params,
      responseType: 'blob'
    })

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `properties_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    return { success: true }
  },

  async fetchSalesControl({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      const response = await axios.get('/properties/sales-control')
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || '获取销控表数据失败'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const getters = {
  allProperties: state => state.properties,
  paginatedProperties: state => state.properties,
  currentProperty: state => state.currentProperty,
  buildings: state => state.buildings,
  unitTypes: state => state.unitTypes,
  pagination: state => state.pagination,
  isLoading: state => state.loading,
  error: state => state.error,

  availableProperties: (state) => {
    return state.properties.filter(property => property.status === '未售')
  },

  soldProperties: (state) => {
    return state.properties.filter(property =>
      ['认购', '签约'].includes(property.status))
  },

  propertiesByBuilding: (state) => {
    return state.properties.reduce((acc, property) => {
      if (!acc[property.building]) {
        acc[property.building] = []
      }
      acc[property.building].push(property)
      return acc
    }, {})
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}